/**
 * studentTutoringAdapter — le compte connecté (`currentUser.object`) et ses séances → ce que
 * « Messages » montre en plus de la liste : ses tuteurs et leurs non-lus, pourquoi il n'en a pas,
 * et les séances où il est inscrit. La carte d'offre est partagée avec les réalisations
 * (`shared/studentOfferAdapter`).
 *
 * Port de l'écran legacy `student/tutoring/MyCoachTabScreen.js`, sur les mêmes champs.
 */
import { type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"
import {
    subjectsForSession,
    toUITutor,
    type ApiLiveOccurrence,
    type ApiLiveOccurrenceTutor,
    type ApiPedagogicalObjective,
} from "../../parent/LiveSessions/liveSessionListAdapter"
import { SUBSCRIPTION_PAUSED_STATUSES } from "../../parent/Subscription/subscriptionAdapter"
import { canReadSubject } from "../shared/studentOfferAdapter"
import { type UISubject, type UITutor } from "../../../ui/types"
import { type UIStudentNoTutor, type UIStudentTutor } from "../../../ui/types/studentTutoring"

/** Une séance du compte (`UserResource.live_sessions`) : son tuteur et les non-lus de ses fils privés. */
export type ApiStudentLiveSession = {
    id?: string | number
    tutor?: ApiLiveOccurrenceTutor | null
    school_subjects?: ApiSchoolSubject[] | null
    /** Par interlocuteur (l'id du tuteur) : les messages qu'il a écrits depuis la dernière lecture. */
    unread_private_messages_count?: Record<string, number | null | undefined> | null
}

/** Forme lâche : le compte vient d'un réducteur en JS, tous les champs sont optionnels. */
export type ApiStudentTutoringUser = {
    id?: string | number
    email?: string
    roles?: string[]
    is_payment_blocked?: boolean
    payment_url?: string | null
    /** Le tuteur principal (`UserLightResource`) ; `null` tant qu'aucun ne lui est attribué. */
    tutor?: ApiLiveOccurrenceTutor | null
    /** Les non-lus du fil du tuteur principal (la route `messenger` sans `tutorId`). */
    tutor_messenger_not_read_count?: number | null
    live_sessions?: ApiStudentLiveSession[] | null
    pedagogical_objectives?: ApiPedagogicalObjective[] | null
    subscription_state?: {
        status?: string
        end_date?: string | null
        planned_start_date?: string | null
    } | null
}

/** Une absence à rattraper (`attendance_flags_to_be_recovered` de `next-live-sessions`). */
export type ApiFlagToBeRecovered = { id?: string | number; is_recoverable_now?: boolean }

const statusOf = (user: ApiStudentTutoringUser | null | undefined) => user?.subscription_state?.status ?? ""
const isSubscriptionPaused = (user: ApiStudentTutoringUser | null | undefined) =>
    SUBSCRIPTION_PAUSED_STATUSES.includes(statusOf(user))

export type StudentTutors = {
    /** Le principal d'abord, puis ceux de ses autres séances, chacun une fois. */
    tutors: UIStudentTutor[]
    /** Le tuteur principal : sa ligne ouvre `messenger` sans paramètre, les autres avec `tutorId`. */
    mainTutorId: UITutor["id"] | null
}

/**
 * Les tuteurs de l'élève — port littéral de `allTutors` (`MyCoachTabScreen.js:225-236`) : le
 * principal (`tutor`), puis les tuteurs de ses séances (`live_sessions[].tutor`), sans le principal
 * ni doublon, dans l'ordre des séances.
 *
 * Non-lus : le principal lit `tutor_messenger_not_read_count` (son fil à lui) ; un autre tuteur lit
 * `unread_private_messages_count[son id]` sur la PREMIÈRE séance qu'il donne — la séance que le
 * legacy retient en dédoublonnant par tuteur. Divergence voulue (DECISIONS, 13/10) : le legacy
 * écrivait sur chaque ligne secondaire la somme de TOUS les tuteurs ; ici chaque ligne a le sien.
 *
 * Matières : celles des séances qu'il donne à l'élève (objectif pédagogique de la séance, sinon
 * les matières de la séance — `subjectsForSession`, la règle des cartes de séance), sans doublon.
 */
export function toStudentTutors(
    user: ApiStudentTutoringUser | null | undefined,
    apiSubjects: ApiSchoolSubject[] = []
): StudentTutors {
    const mainTutor = user?.tutor?.id != null ? user.tutor : null
    const sessions = (user?.live_sessions ?? []).filter((session) => session?.tutor?.id != null)

    const firstSessionByTutor = new Map<string, ApiStudentLiveSession>()
    for (const session of sessions) {
        const key = String(session.tutor?.id)
        if (!firstSessionByTutor.has(key)) firstSessionByTutor.set(key, session)
    }

    const others = [...firstSessionByTutor.values()]
        .map((session) => session.tutor as ApiLiveOccurrenceTutor)
        .filter((tutor) => mainTutor == null || String(tutor.id) !== String(mainTutor.id))

    const subjectsOf = (tutorId: string | number | undefined): UISubject[] => {
        const byId = new Map<string, UISubject>()
        for (const session of sessions) {
            if (String(session.tutor?.id) !== String(tutorId)) continue
            for (const subject of subjectsForSession(session.id, user, apiSubjects, session.school_subjects)) {
                if (!byId.has(String(subject.id))) byId.set(String(subject.id), subject)
            }
        }
        return [...byId.values()]
    }

    const row = (raw: ApiLiveOccurrenceTutor, unread: number | null | undefined): UIStudentTutor => {
        const subjects = subjectsOf(raw.id)
        return {
            tutor: toUITutor(raw),
            unreadCount: unread ?? 0,
            ...(subjects.length > 0 ? { subjects } : {}),
        }
    }

    const tutors = [
        ...(mainTutor != null ? [row(mainTutor, user?.tutor_messenger_not_read_count)] : []),
        ...others.map((tutor) =>
            row(tutor, firstSessionByTutor.get(String(tutor.id))?.unread_private_messages_count?.[String(tutor.id)])
        ),
    ]

    return { tutors, mainTutorId: mainTutor?.id ?? null }
}

/**
 * La pastille de « Messages » : les messages non lus, et rien d'autre (Wajih, 19/10). Le compte de la
 * coquille legacy (`studentTabScreens.js`, `countTutorsPrivateMessages`) : par tuteur, les non-lus de
 * son fil privé sur la PREMIÈRE séance qu'il donne (`unread_private_messages_count[son id]`). Le fil
 * du tuteur principal est le même fil que celui de sa séance (`createThreadIdsForUser`) : il n'est
 * pas recompté par `tutor_messenger_not_read_count`.
 *
 * Divergences voulues : la visio du jour et les lives qui commencent bientôt ne comptent plus (les
 * lives ont quitté l'onglet le 18/10) ; une séance sans tuteur ne rend plus toute la somme illisible
 * (le legacy y additionnait `undefined`, et la pastille disparaissait).
 */
export function studentUnreadMessagesCount(user: ApiStudentTutoringUser | null | undefined): number {
    const counted = new Set<string>()
    let unread = 0
    for (const session of user?.live_sessions ?? []) {
        const tutorId = session?.tutor?.id
        if (tutorId == null || counted.has(String(tutorId))) continue
        counted.add(String(tutorId))
        unread += Math.max(0, session.unread_private_messages_count?.[String(tutorId)] ?? 0)
    }
    return unread
}

/**
 * Pourquoi l'élève n'a pas de tuteur principal — les trois branches de `MyCoachTabScreen.js:265-355`,
 * dans leur ordre : une formule qui donne un tuteur (`have_tutor`) → il attend son groupe ; sinon,
 * pas en pause → pas d'abonnement ; en pause → la reprise (`subscription_state.planned_start_date`).
 * `undefined` : il a un tuteur.
 */
export function studentNoTutor(user: ApiStudentTutoringUser | null | undefined): UIStudentNoTutor | undefined {
    if (user?.tutor?.id != null) return undefined
    if (canReadSubject(user?.roles, "have_tutor")) return { kind: "awaiting" }
    if (!isSubscriptionPaused(user)) return { kind: "unsubscribed" }
    const resumeDate = user?.subscription_state?.planned_start_date
    return resumeDate ? { kind: "paused", resumeDate } : { kind: "paused" }
}

/**
 * Les occurrences où l'élève est inscrit — la garde du « GO ! » (`StudentLiveCardComponent.answerGo`) :
 * la séance doit le compter parmi ses élèves (`live_session.students`), sinon « Tu n'es pas inscrit
 * à cette session live ».
 */
export function enrolledOccurrenceIds(
    occurrences: ApiLiveOccurrence[] | null | undefined,
    userId: string | number | null | undefined
): string[] {
    if (userId == null) return []
    return (occurrences ?? [])
        .filter((occurrence) => occurrence?.id != null)
        .filter((occurrence) =>
            (occurrence.live_session?.students ?? []).some((student) => String(student?.id) === String(userId))
        )
        .map((occurrence) => String(occurrence.id))
}

/** « Planifier un rattrapage » : les absences à rattraper, et celles rattrapables dès maintenant. */
export function recoveryCounts(flags: ApiFlagToBeRecovered[] | null | undefined): {
    toRecover: number
    recoverableNow: number
} {
    const list = flags ?? []
    return { toRecover: list.length, recoverableNow: list.filter((flag) => flag?.is_recoverable_now).length }
}
